import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { Divider } from 'primereact/divider';
import { Badge } from 'primereact/badge';
import { Toast } from 'primereact/toast';
import CheckoutDialog from '../components/CheckoutDialog';

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const toast = useRef(null);
  
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [comments, setComments] = useState('');
  const [checkoutVisible, setCheckoutVisible] = useState(false);

  // Mock product data - in real app this would come from API
  const products = {
    'henbury-polo': {
      id: 'henbury-polo',
      name: 'Henbury Ladies Polo HB401 with logo',
      price: 22.50,
      description: 'Henbury HB401 Women\'s 65/35 polo Stand up collar for increased shape retention. Price includes P&P to home address.',
      longDescription: `Washable at 60°C.
Cuffed sleeves.
3 button placket with self-coloured buttons.
Side vents.
Longer sleeves and body length than the Fruit of the Loom`,
      fabric: '65% Polyester, 35% Cotton',
      weight: '200 gsm',
      sizes: ['XS-8', 'S-10', 'M-12', 'L-14', 'XL-16', '2XL-18', '3XL-20'],
      colors: ['Black', 'Navy', 'Sky', 'White'],
      features: [
        'Washable at 60°C',
        'Cuffed sleeves',
        '3 button placket with self-coloured buttons',
        'Side vents',
        'SE logo included',
        'Free postage - allow 2 weeks for delivery'
      ],
      images: ['/api/placeholder/400/400'],
      inStock: true,
      category: 'clothing-merchandise'
    },
    'gift-voucher-rides': {
      id: 'gift-voucher-rides',
      name: 'Gift voucher for on-line orders',
      price: 25.00,
      description: 'Credit Vouchers to give to your friends!',
      longDescription: 'Purchase gift vouchers that can be used for event entries, merchandise, and other services on our website. Perfect for birthdays, holidays, or any special occasion.',
      features: [
        'Can be used for event registrations',
        'Valid for merchandise purchases', 
        'No expiry date',
        'Digital delivery',
        'Personal message included'
      ],
      images: ['/api/placeholder/400/400'],
      inStock: true,
      category: 'gift-tokens'
    },
    'unisex-hoodie': {
      id: 'unisex-hoodie',
      name: 'Unisex hoodie JH001 with logo and website on back',
      price: 35.00,
      description: 'Twin needle stitching detailing. Double fabric hood with self coloured cords. Kangaroo pouch pocket. Ribbed cuffs and hem. Price includes P&P to home address.',
      fabric: '80% Cotton, 20% Polyester',
      weight: '280 gsm',
      sizes: ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'],
      colors: ['Black', 'Navy', 'Grey', 'Royal Blue'],
      features: [
        'Twin needle stitching detailing',
        'Double fabric hood with self coloured cords',
        'Kangaroo pouch pocket',
        'Ribbed cuffs and hem',
        'SE logo on front and website on back',
        'Free postage - allow 2 weeks for delivery'
      ],
      images: ['/api/placeholder/400/400'],
      inStock: true,
      category: 'clothing-merchandise'
    },
    'baseball-cap': {
      id: 'baseball-cap',
      name: 'Baseball Cap Ultimate 5 panel cap BC015 with logo',
      price: 18.00,
      description: 'Contemporary styling. Rip-Strip size adjuster. With SE logo. Price includes P&P to home address.',
      sizes: ['One Size'],
      colors: ['Black', 'Navy', 'Royal Blue'],
      features: [
        'Contemporary styling',
        'Rip-Strip size adjuster',
        'SE logo embroidered',
        'Free postage - allow 2 weeks for delivery'
      ],
      images: ['/api/placeholder/400/400'],
      inStock: true,
      category: 'clothing-merchandise'
    }
  };

  const product = products[productId];

  if (!product) {
    return (
      <div className="text-center py-8">
        <h2>Product not found</h2>
        <Button
          label="Back to Shop"
          icon="pi pi-arrow-left"
          onClick={() => navigate('/shop')}
        />
      </div>
    );
  }

  const sizeOptions = product.sizes?.map(size => ({ label: size, value: size })) || [];
  const colorOptions = product.colors?.map(color => ({ label: color, value: color })) || [];

  const handleAddToCart = () => {
    // Validate required selections
    if (product.sizes && product.sizes.length > 0 && !selectedSize) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select a size',
        life: 3000
      });
      return;
    }
    if (product.colors && product.colors.length > 0 && !selectedColor) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select a color',
        life: 3000
      });
      return;
    }

    // Create product for checkout
    const checkoutProduct = {
      ...product,
      selectedSize,
      selectedColor,
      quantity,
      comments,
      price: product.price * quantity
    };

    setCheckoutVisible(true);
  };

  const totalPrice = product.price * quantity;

  return (
    <div>
      <Toast ref={toast} />
      {/* Back Button */}
      <div className="flex justify-content-start mb-3">
        <Button 
          icon="pi pi-arrow-left" 
          label="Back to Shop" 
          className="p-button-text" 
          onClick={() => navigate('/shop')}
        />
      </div>

      <div className="grid">
        {/* Product Images */}
        <div className="col-12 md:col-6">
          <Card>
            <div className="text-center">
              <img 
                src={product.images[0]} 
                alt={product.name}
                className="w-full border-round"
                style={{ maxHeight: '400px', objectFit: 'cover' }}
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjhGOUZBIi8+CjxwYXRoIGQ9Ik0yMDAgMTQwTDI2MCAyMDBIMTQwTDIwMCAxNDBaIiBmaWxsPSIjREVFMkU2Ii8+CjxjaXJjbGUgY3g9IjIwMCIgY3k9IjIwMCIgcj0iODAiIHN0cm9rZT0iI0RFRTJFNiIgc3Ryb2tlLXdpZHRoPSI0IiBmaWxsPSJub25lIi8+Cjx0ZXh0IHg9IjIwMCIgeT0iMjgwIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmaWxsPSIjNjY3MDdCIiBmb250LXNpemU9IjI0Ij5Qcm9kdWN0IEltYWdlPC90ZXh0Pgo8L3N2Zz4K';
                }}
              />
            </div>
          </Card>
        </div>

        {/* Product Details */}
        <div className="col-12 md:col-6">
          <div className="mb-4">
            <h1 className="mt-0 mb-2 se-text-primary">{product.name}</h1>
            <div className="flex justify-content-between align-items-center mb-3">
              <div className="text-3xl font-bold se-text-primary">£{product.price.toFixed(2)}</div>
              {product.inStock ? (
                <Badge value="In Stock" severity="success" />
              ) : (
                <Badge value="Out of Stock" severity="danger" />
              )}
            </div>
            <p className="text-600 mb-3">{product.description}</p>
          </div>

          {/* Product Options */}
          <Card className="mb-4">
            <div className="grid">
              {/* Size Selection */}
              {product.sizes && product.sizes.length > 0 && (
                <div className="col-12 md:col-6">
                  <div className="se-form-group">
                    <label className="se-form-label">Size *</label>
                    <Dropdown
                      value={selectedSize}
                      options={sizeOptions}
                      onChange={(e) => setSelectedSize(e.value)}
                      placeholder="Select size"
                      className="w-full"
                    />
                  </div>
                </div>
              )}

              {/* Color Selection */}
              {product.colors && product.colors.length > 0 && (
                <div className="col-12 md:col-6">
                  <div className="se-form-group">
                    <label className="se-form-label">Color *</label>
                    <Dropdown
                      value={selectedColor}
                      options={colorOptions}
                      onChange={(e) => setSelectedColor(e.value)}
                      placeholder="Select color"
                      className="w-full"
                    />
                  </div>
                </div>
              )}

              {/* Quantity */}
              <div className="col-12 md:col-6">
                <div className="se-form-group">
                  <label className="se-form-label">Quantity</label>
                  <InputNumber
                    value={quantity}
                    onValueChange={(e) => setQuantity(e.value)}
                    min={1}
                    max={10}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Comments */}
              <div className="col-12">
                <div className="se-form-group">
                  <label className="se-form-label">Comments</label>
                  <InputTextarea
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder="Special requests, personalization, etc."
                    rows={3}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            {/* Total Price */}
            <Divider />
            <div className="flex justify-content-between align-items-center mb-3">
              <span className="font-bold text-lg">Total:</span>
              <span className="font-bold text-xl se-text-primary">£{totalPrice.toFixed(2)}</span>
            </div>

            {/* Add to Cart Button */}
            <Button
              label="Add to Cart"
              icon="pi pi-shopping-cart"
              className="w-full p-button-primary"
              onClick={handleAddToCart}
              disabled={!product.inStock}
            />
          </Card>
        </div>
      </div>

      {/* Product Information */}
      <div className="grid">
        <div className="col-12 md:col-6">
          <Card>
            <h3 className="mt-0 mb-3">Product Details</h3>
            {product.longDescription && (
              <div className="mb-3 text-left">
                <p className="whitespace-pre-line text-left">{product.longDescription}</p>
              </div>
            )}
            
            <div className="text-left">
              {product.fabric && (
                <div className="mb-2 flex align-items-start">
                  <strong className="mr-2">Fabric:</strong> 
                  <span>{product.fabric}</span>
                </div>
              )}
              
              {product.weight && (
                <div className="mb-2 flex align-items-start">
                  <strong className="mr-2">Weight:</strong> 
                  <span>{product.weight}</span>
                </div>
              )}
            </div>
          </Card>
        </div>

        <div className="col-12 md:col-6">
          <Card>
            <h3 className="mt-0 mb-3">Features</h3>
            <ul className="list-none p-0 m-0">
              {product.features.map((feature, index) => (
                <li key={index} className="mb-2 flex align-items-start">
                  <i className="pi pi-check-circle text-green-500 mr-2 mt-1"></i>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>

      {/* Checkout Dialog */}
      <CheckoutDialog
        visible={checkoutVisible}
        onHide={() => setCheckoutVisible(false)}
        item={{
          ...product,
          selectedSize,
          selectedColor,
          quantity,
          comments,
          price: totalPrice
        }}
        itemType="product"
      />
    </div>
  );
};

export default ProductDetail;